import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import './EditCertificationModal.scss';
import { Button, Chip, Input, LoadingPlaceholder, Modal, Select } from '@trackman/web-shared-components';
import Subtitle from 'components/TextComps/Subtitle';
import { useServices, useStores } from 'index';
import { useCallback, useMemo, useState } from 'react';
import { AdminCertification } from 'types/admin/modules';
import { BannerImage } from '../../BannerImage/BannerImage';

type Props = {
  open: boolean;
  certification: AdminCertification;
  newCertifications: AdminCertification[];
  onClose: (certification: (AdminCertification & { newId: string }) | undefined) => void;
};

export const EditCertificationModal = ({ open, certification, newCertifications, onClose }: Props) => {
  const editCertificationService = useServices().editCertificationService;
  const mediaService = useServices().mediaService;
  const notificationStore = useStores().notificationStore;

  const [certificationTitle, setCertificationTitle] = useState<string | undefined>(certification.title ?? undefined);
  const [area9Entrypoint, setArea9Entrypoint] = useState<string | undefined>(certification.enterpoint ?? undefined);
  const [prerequisiteIds, setPrerequisiteIds] = useState<string[] | undefined>(certification.prerequisites ?? undefined);
  const [shortDescription, setShortDescription] = useState<string | undefined>(certification.shortDescription ?? undefined);
  const [longDescription, setLongDescription] = useState<string | undefined>(certification.fullDescription ?? undefined);
  const [initialId, setInitialId] = useState<string | undefined>(certification.id ?? undefined);
  const [newId, setNewId] = useState<string | undefined>(certification.id ?? undefined);
  const [badgeImageId, setBadgeImageId] = useState<string | undefined>(certification.badgeImageId ?? undefined);
  const [pdfBadgeImageId, setPdfBadgeImageId] = useState<string | undefined>(certification.pdfBadgeImageId ?? undefined);

  const editCertification = useQuery({
    queryKey: ['availablePrerequisites', certification.id],
    queryFn: () => editCertificationService.getAvailablePrerequisites(certification.id),
    enabled: !!certification.id,
    staleTime: Infinity,
  });

  const badgeImage = useQuery({
    queryKey: ['certificationBadgeImage', badgeImageId],
    queryFn: () => mediaService.getMedia(badgeImageId),
    enabled: !!badgeImageId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const queryClient = useQueryClient();
  const recurlyStatus = useQuery({
    queryKey: ['recurlyItemStatus', certification.id],
    queryFn: () => editCertificationService.getRecurlyItemStatus(certification.id),
    enabled: !!certification.id,
    staleTime: Infinity,
  });

  const syncWithCatalog = useMutation({
    mutationFn: () => editCertificationService.syncWithCatalog(certification.id),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['recurlyItemStatus', certification.id] });
      notificationStore.addToast('success', 'Certification synced with Recurly catalog', 'success');
    },
    onError: (error: Error) => {
      notificationStore.addToast('error', error?.message ?? 'Failed to synchronize with Recurly', 'error');
    },
  });

  const handleOpenInRecurly = useCallback(() => {
    const status = recurlyStatus.data;
    if (status?.itemUrl) {
      window.open(status.itemUrl, '_blank', 'noopener,noreferrer');
    }
  }, [recurlyStatus.data]);

  const handleClose = useCallback(() => {
    resetLocalState();
    onClose(undefined);
  }, [onClose]);
  const handleSaveCertification = useCallback(() => {
    const payload: AdminCertification & { newId: string } = {
      ...certification,
      title: certificationTitle ?? '',
      enterpoint: area9Entrypoint ?? '',
      prerequisites: prerequisiteIds ?? [],
      shortDescription: shortDescription ?? '',
      fullDescription: longDescription ?? '',
      id: initialId ?? '',
      newId: newId ?? '',
      badgeImageId: badgeImageId ?? undefined,
    };
    onClose(payload);
    resetLocalState();
  }, [
    certification,
    certificationTitle,
    area9Entrypoint,
    prerequisiteIds,
    shortDescription,
    longDescription,
    initialId,
    newId,
    badgeImageId,
    onClose,
  ]);
  const prerequisitesList = useMemo(() => {
    if (!newCertifications && !editCertification.data) {
      return [];
    } else if (!newCertifications) {
      return editCertification.data;
    } else if (!editCertification.data) {
      return newCertifications;
    } else {
      return [...newCertifications, ...editCertification.data];
    }
  }, [newCertifications, editCertification.data]);

  const prerequisiteName = useCallback(
    (id: string) => {
      return prerequisitesList?.find((c) => c.id === id)?.title;
    },
    [prerequisitesList]
  );

  const resetLocalState = () => {
    setCertificationTitle(undefined);
    setArea9Entrypoint(undefined);
    setPrerequisiteIds([]);
    setShortDescription(undefined);
    setLongDescription(undefined);
    setInitialId(undefined);
    setNewId(undefined);
    setBadgeImageId(undefined);
  };

  const shouldDisableSaveButton = !certificationTitle || !area9Entrypoint || !shortDescription || !badgeImageId;

  return (
    <Modal
      className='edit-certification-modal'
      cancelBtn={{
        label: 'Cancel',
        onClick: handleClose,
      }}
      confirmBtn={{
        label: 'Save',
        onClick: handleSaveCertification,
        disabled: shouldDisableSaveButton,
      }}
      onClose={handleClose}
      title={certificationTitle ? certificationTitle : 'Edit certification'}
      open={open}
    >
      {!editCertification.isPending ? (
        <>
          <div className='edit-certification-modal-section'>
            <Subtitle>
              <>General Information</>
            </Subtitle>
            <div>
              <Input
                label='Certification Title'
                value={certificationTitle}
                required
                onChange={(e) => setCertificationTitle(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />

              <Input
                label='Area9 Entrypoint'
                value={area9Entrypoint}
                required
                onChange={(e) => setArea9Entrypoint(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            <div className='prerequisites-wrapper'>
              <Select
                label='Prerequisites'
                value={prerequisiteIds}
                hint='Certifications required before completing this one.'
                multiple
                selectOptions={prerequisitesList?.map((course) => {
                  return {
                    label: course.title,
                    value: course.id,
                  };
                })}
                onChange={(e) => setPrerequisiteIds(e.target.value as string[])}
              />
              {prerequisiteIds && prerequisiteIds?.length > 0 ? (
                <div className='prerequisites'>
                  {prerequisiteIds?.map((id) => (
                    <Chip
                      className='prerequisite'
                      color='primary'
                      key={id}
                      label={prerequisiteName(id) ?? ''}
                      onDelete={() => setPrerequisiteIds(prerequisiteIds.filter((prerequisiteId) => id !== prerequisiteId))}
                    />
                  ))}
                </div>
              ) : (
                <></>
              )}
            </div>
            <Input
              label='Existing certificate id'
              value={newId}
              onChange={(e) => setNewId(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              className='exisiting-certificate-id'
            />
          </div>

          <div className='edit-certification-modal-section badge-image-section'>
            <Subtitle>
              <>Certificate badge*</>
            </Subtitle>
            <BannerImage image={badgeImage.data} disabled={false} handleSelectedImage={setBadgeImageId} imageVariant='square' />
          </div>

          <div className='edit-certification-modal-section'>
            <Subtitle>
              <>About the Certification</>
            </Subtitle>
            <Input
              label='Short Description'
              required
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value as string)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
            {/* <TextArea
              label='About this certification'
              required
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value as string)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            /> */}
          </div>

          {recurlyStatus.data && (
            <div className='edit-certification-modal-section'>
              <Subtitle>
                <>Recurly</>
              </Subtitle>
              <div>
                {syncWithCatalog.isPending ? (
                  <p>Synchronizing...</p>
                ) : recurlyStatus.data.exists ? (
                  <Button variant='contained' intent='secondary' onClick={handleOpenInRecurly}>
                    Open in Recurly
                  </Button>
                ) : (
                  <div>
                    <p>
                      This resource is not found in Recurly. You can synchronize it to create the corresponding item in
                      the Recurly catalog.
                    </p>
                    <Button variant='contained' intent='secondary' onClick={() => syncWithCatalog.mutate()}>
                      Synchronize
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* <div className='edit-certification-modal-section'>
            <Subtitle>
              <>What you'll get</>
            </Subtitle>
            <ul>
              <li>Download your certificate as a PDF and print it</li>
              <li>Add it to your professional portfolio or resume</li>
              <li>Frame it for instant bragging rights!</li>
            </ul>
          </div>

          <div className='edit-certification-modal-section'>
            <Subtitle>
              <>Exam details</>
            </Subtitle>
            <ul>
              <li>
                $250 <span>(Get 50% off with Premium)</span>
              </li>
              <li>3 attemps</li>
              <li>{certification.numberOfQuestions ?? 0} questions</li>
              <li>
                {prerequisiteIds && prerequisiteIds?.length > 0
                  ? `Prerequisites for this certification: ${prerequisiteIds.map((id) => getPrerequisiteName(id)).join(', ')}`
                  : 'No prerequisites needed'}
              </li>
            </ul>
          </div> */}
        </>
      ) : (
        <LoadingPlaceholder />
      )}
    </Modal>
  );
};
